import React from "react";
import {
  FaChalkboardTeacher,
  FaClock,
  FaEuroSign,
  FaInfoCircle,
  FaLayerGroup,
  FaUsers,
} from "react-icons/fa";
import { FiMapPin, FiX } from "react-icons/fi";

import {
  clamp,
  cn,
  fillPercentage,
  getHexColor,
  horairesOf,
  isGroupFull,
  lighten,
  sanitizeHtml,
  teachersOf,
  totalTarif,
} from "./groupPlanningUtils";

function InfoBox({ icon: Icon, title, children }) {
  return (
    <div
      className="
        rounded-3xl border border-slate-200
        bg-slate-50 p-4
        transition duration-200
        hover:border-orange-200 hover:bg-orange-50/40
        dark:border-white/10 dark:bg-white/5
        dark:hover:border-orange-500/30 dark:hover:bg-orange-500/5
      "
    >
      <div className="mb-3 flex items-center gap-3 text-sm font-bold text-slate-900 dark:text-white">
        <span
          className="
            flex h-10 w-10 shrink-0 items-center justify-center
            rounded-2xl bg-orange-100 text-orange-600
            dark:bg-orange-500/10 dark:text-orange-400
          "
        >
          <Icon />
        </span>

        <span>{title}</span>
      </div>

      <div className="text-sm leading-7 text-slate-600 dark:text-slate-300">
        {children}
      </div>
    </div>
  );
}

export default function GroupModal({ open, onClose, group, dojo }) {
  if (!open || !group) return null;

  const baseColor = getHexColor(group.color_groupe);
  const lightColor = lighten(baseColor, 0.72);
  const percentage = fillPercentage(group);
  const full = isGroupFull(group);
  const horaires = horairesOf(group);
  const teachers = teachersOf(group);
  const total = totalTarif(group);
  const sanitizedInfo = sanitizeHtml(group.info_groupe);

  return (
    <div
      className="
        fixed inset-0 z-[70]
        flex items-center justify-center
        bg-slate-950/75 px-3 py-6
        backdrop-blur-sm
      "
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="group-modal-title"
    >
      <div
        className="
          relative max-h-[92vh] w-full max-w-4xl
          overflow-hidden rounded-[2rem]
          border border-slate-200 bg-white
          shadow-2xl
          dark:border-white/10 dark:bg-slate-950
        "
        onClick={(event) => event.stopPropagation()}
      >
        {/* Bouton de fermeture */}
        <button
          type="button"
          onClick={onClose}
          className="
            absolute right-4 top-4 z-20
            flex h-11 w-11 items-center justify-center
            rounded-full
            border border-slate-200 bg-white/90
            text-slate-700 shadow-lg backdrop-blur
            transition duration-200
            hover:rotate-90 hover:border-orange-300
            hover:bg-orange-50 hover:text-orange-600
            focus:outline-none focus:ring-4 focus:ring-orange-500/20

            dark:border-white/10 dark:bg-slate-900/90
            dark:text-slate-200
            dark:hover:border-orange-500/40
            dark:hover:bg-orange-500/10
            dark:hover:text-orange-400
          "
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          {/* En-tête coloré */}
          <div
            className="relative overflow-hidden p-6 sm:p-8"
            style={{
              background: `linear-gradient(135deg, ${baseColor} 0%, ${lightColor} 100%)`,
            }}
          >
            <div className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white/25 blur-3xl" />

            <div className="pointer-events-none absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-white/20 blur-3xl" />

            <div className="relative pr-12 text-slate-950">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <span
                  className="
                    rounded-full border border-white/30
                    bg-white/75 px-3 py-1
                    text-xs font-bold text-slate-900
                    shadow-sm backdrop-blur
                  "
                >
                  {dojo?.dojo_nom || "Dojo"}
                </span>

                {group.discipline_groupe && (
                  <span
                    className="
                      rounded-full border border-white/30
                      bg-white/75 px-3 py-1
                      text-xs font-bold text-slate-900
                      shadow-sm backdrop-blur
                    "
                  >
                    {group.discipline_groupe}
                  </span>
                )}

                {full && (
                  <span className="rounded-full bg-rose-600 px-3 py-1 text-xs font-bold uppercase text-white shadow-sm">
                    Complet
                  </span>
                )}
              </div>

              <h3
                id="group-modal-title"
                className="
                  mt-5 font-barlow text-3xl
                  font-extrabold uppercase leading-tight
                  text-slate-950 drop-shadow-sm
                  sm:text-4xl
                "
              >
                {group.nom}
              </h3>

              {group.annees_naissance && (
                <p className="mt-2 text-sm font-bold italic text-slate-900">
                  {group.annees_naissance}
                </p>
              )}

              {/* Statistiques */}
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <div className="rounded-3xl border border-white/30 bg-white/75 p-4 shadow-sm backdrop-blur">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-600">
                    Effectif
                  </p>

                  <p className="mt-1 font-barlow text-2xl font-extrabold text-slate-950">
                    {group.adherentCount || 0}/{group.quota_groupe || 0}
                  </p>
                </div>

                <div className="rounded-3xl border border-white/30 bg-white/75 p-4 shadow-sm backdrop-blur">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-600">
                    Remplissage
                  </p>

                  <p
                    className={cn(
                      "mt-1 font-barlow text-2xl font-extrabold",
                      percentage >= 100
                        ? "text-rose-700"
                        : "text-slate-950"
                    )}
                  >
                    {percentage} %
                  </p>
                </div>

                <div className="rounded-3xl border border-white/30 bg-white/75 p-4 shadow-sm backdrop-blur">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-600">
                    Tarif annuel
                  </p>

                  <p className="mt-1 font-barlow text-2xl font-extrabold text-slate-950">
                    {total} €
                  </p>
                </div>
              </div>

              {/* Barre de remplissage */}
              <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/55 shadow-inner">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-500",
                    percentage >= 100
                      ? "bg-rose-600"
                      : "bg-orange-600"
                  )}
                  style={{
                    width: `${clamp(percentage, 0, 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="space-y-5 p-6 text-slate-900 dark:text-white sm:p-8">
            {/* Adresse */}
            {dojo?.dojo_adresse && (
              <div
                className="
                  flex items-start gap-3 rounded-3xl
                  border border-slate-200 bg-slate-50 p-4
                  text-sm text-slate-700
                  dark:border-white/10 dark:bg-white/5
                  dark:text-slate-300
                "
              >
                <span
                  className="
                    flex h-10 w-10 shrink-0 items-center justify-center
                    rounded-2xl bg-orange-100 text-orange-600
                    dark:bg-orange-500/10 dark:text-orange-400
                  "
                >
                  <FiMapPin />
                </span>

                <span className="pt-1 leading-7">
                  <strong className="text-slate-950 dark:text-white">
                    Adresse :
                  </strong>{" "}
                  {dojo.dojo_adresse}
                </span>
              </div>
            )}

            {/* Informations principales */}
            <div className="grid gap-4 md:grid-cols-2">
              <InfoBox icon={FaClock} title="Horaires">
                {horaires.length ? (
                  <ul className="space-y-1">
                    {horaires.map((horaire, index) => (
                      <li key={`${horaire}-${index}`} className="flex gap-2">
                        <span className="text-orange-500">•</span>
                        <span>{horaire}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="italic opacity-80">Non renseigné.</p>
                )}
              </InfoBox>

              <InfoBox icon={FaEuroSign} title="Tarifs">
                <p>Licence : {group.tarif_licence || 0} €</p>
                <p>Cotisation : {group.tarif_cotisation || 0} €</p>

                <p className="mt-2 font-extrabold text-orange-700 dark:text-orange-400">
                  Total : {total} €
                </p>
              </InfoBox>

              <InfoBox icon={FaChalkboardTeacher} title="Enseignants">
                {teachers.length ? teachers.join(" • ") : "Non renseigné"}
              </InfoBox>

              <InfoBox icon={FaLayerGroup} title="Discipline">
                {group.discipline_groupe || "Non renseignée"}
              </InfoBox>

              <InfoBox icon={FaUsers} title="Effectif">
                <p>
                  {group.adherentCount || 0} inscrit(s) /{" "}
                  {group.quota_groupe || 0}
                </p>

                {full && (
                  <span
                    className="
                      mt-2 inline-flex rounded-full
                      bg-rose-100 px-3 py-1
                      text-xs font-bold uppercase text-rose-700
                      dark:bg-rose-500/10 dark:text-rose-300
                    "
                  >
                    Groupe complet
                  </span>
                )}
              </InfoBox>
            </div>

            {/* Informations complémentaires */}
            {group.info_groupe && (
              <div
                className="
                  rounded-3xl border border-slate-200
                  bg-slate-50 p-5
                  dark:border-white/10 dark:bg-white/5
                "
              >
                <div className="mb-4 flex items-center gap-3 font-bold text-slate-950 dark:text-white">
                  <span
                    className="
                      flex h-10 w-10 shrink-0 items-center justify-center
                      rounded-2xl bg-orange-100 text-orange-600
                      dark:bg-orange-500/10 dark:text-orange-400
                    "
                  >
                    <FaInfoCircle />
                  </span>

                  <span>Informations complémentaires</span>
                </div>

                <div
                  className={cn(
                    "prose prose-slate max-w-none dark:prose-invert",
                    "text-sm leading-7 text-slate-600 dark:text-slate-300",

                    "prose-p:my-3",
                    "prose-li:my-1",

                    "prose-strong:text-slate-950",
                    "dark:prose-strong:!text-white",

                    "prose-b:text-slate-950",
                    "dark:prose-b:!text-white",

                    "prose-a:font-bold",
                    "prose-a:text-orange-600",
                    "hover:prose-a:text-orange-700",

                    "dark:prose-a:text-orange-400",
                    "dark:hover:prose-a:text-orange-300"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: sanitizedInfo,
                  }}
                />
              </div>
            )}

            {/* Bouton fermeture */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="
                  inline-flex items-center justify-center
                  rounded-full border border-orange-600
                  bg-orange-600 px-6 py-3
                  text-sm font-bold text-white
                  shadow-lg transition duration-200
                  hover:-translate-y-0.5
                  hover:border-orange-700 hover:bg-orange-700
                  hover:shadow-xl
                  focus:outline-none focus:ring-4 focus:ring-orange-500/25

                  dark:border-orange-500
                  dark:bg-orange-500
                  dark:hover:border-orange-400
                  dark:hover:bg-orange-400
                "
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}